import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';

const durationOptions = [
  { label: '1 Month', days: 30 },
  { label: '3 Months', days: 90 },
  { label: '6 Months', days: 180 },
  { label: '1 Year', days: 365 }
];

const defaultPlanForm = {
  name: '',
  code: '',
  maxPosterAds: '',
  maxProductAds: '',
  maxProductsPerAd: 1,
  maxSubImagesPerMainAd: '',
  maxAdsDurationDays: 10,
  durationDays: 30,
  price: '',
  isActive: true
};

const SuperAdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState(defaultPlanForm);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/super/plans');
      setPlans(response.data.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching plans' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      code: plan.code,
      maxPosterAds: plan.maxPosterAds ?? plan.maxMainAds,
      maxProductAds: plan.maxProductAds ?? plan.maxMainAds,
      maxProductsPerAd: plan.maxProductsPerAd || 1,
      maxSubImagesPerMainAd: plan.maxSubImagesPerMainAd,
      maxAdsDurationDays: plan.maxAdsDurationDays,
      durationDays: plan.durationDays || 30,
      price: plan.price,
      isActive: plan.isActive
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const posterLimit = Number(formData.maxPosterAds || 0);
      const productLimit = Number(formData.maxProductAds || 0);
      const payload = {
        ...formData,
        maxMainAds: posterLimit + productLimit,
        maxPosterAds: posterLimit,
        maxProductAds: productLimit,
        maxProductsPerAd: Number(formData.maxProductsPerAd || 1),
        maxSubImagesPerMainAd: Number(formData.maxSubImagesPerMainAd),
        maxAdsDurationDays: Number(formData.maxAdsDurationDays || 10),
        durationDays: Number(formData.durationDays || 30),
        price: Number(formData.price || 0)
      };

      if (editingPlan) {
        await api.put(`/api/super/plans/${editingPlan._id}`, payload);
        setMessage({ type: 'success', text: 'Plan updated successfully' });
      } else {
        await api.post('/api/super/plans', payload);
        setMessage({ type: 'success', text: 'Plan created successfully' });
      }
      setShowModal(false);
      fetchPlans();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error saving plan' });
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Plans Management</h1>
          <button className="btn btn-primary" onClick={() => {
            setEditingPlan(null);
            setFormData(defaultPlanForm);
            setShowModal(true);
          }}>
            Create Plan
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Max Ads</th>
                <th>Poster Ads</th>
                <th>Product Offers</th>
                <th>Products / Offer</th>
                <th>Validity</th>
                <th>Max Sub Images</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(plan => (
                <tr key={plan._id}>
                  <td>{plan.name}</td>
                  <td>{plan.code}</td>
                  <td>{plan.maxMainAds}</td>
                  <td>{plan.maxPosterAds ?? plan.maxMainAds}</td>
                  <td>{plan.maxProductAds ?? plan.maxMainAds}</td>
                  <td>{plan.maxProductsPerAd || 1}</td>
                  <td>{plan.durationDays || 30} days</td>
                  <td>{plan.maxSubImagesPerMainAd}</td>
                  <td>₹{plan.price}</td>
                  <td>{plan.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleEdit(plan)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingPlan ? 'Edit Plan' : 'Create Plan'}
                </h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Plan Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    placeholder="PLAN_1"
                  />
                </div>
                <div className="form-group">
                  <label>Poster Ads *</label>
                  <input
                    type="number"
                    value={formData.maxPosterAds}
                    onChange={(e) => setFormData({ ...formData, maxPosterAds: e.target.value })}
                    required
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Product Offers *</label>
                  <input
                    type="number"
                    value={formData.maxProductAds}
                    onChange={(e) => setFormData({ ...formData, maxProductAds: e.target.value })}
                    required
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Products Per Offer *</label>
                  <input
                    type="number"
                    value={formData.maxProductsPerAd}
                    onChange={(e) => setFormData({ ...formData, maxProductsPerAd: e.target.value })}
                    required
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Max Sub Images Per Ad *</label>
                  <input
                    type="number"
                    value={formData.maxSubImagesPerMainAd}
                    onChange={(e) => setFormData({ ...formData, maxSubImagesPerMainAd: e.target.value })}
                    required
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Max Ad Duration (Days)</label>
                  <input
                    type="number"
                    value={formData.maxAdsDurationDays}
                    onChange={(e) => setFormData({ ...formData, maxAdsDurationDays: e.target.value })}
                    min="1"
                    max="365"
                  />
                </div>
                <div className="form-group">
                  <label>Plan Validity *</label>
                  <select
                    value={durationOptions.some((option) => option.days === Number(formData.durationDays)) ? String(formData.durationDays) : 'custom'}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setFormData({ ...formData, durationDays: '' });
                      } else {
                        setFormData({ ...formData, durationDays: Number(e.target.value) });
                      }
                    }}
                    required
                  >
                    {durationOptions.map((option) => (
                      <option key={option.days} value={option.days}>{option.label}</option>
                    ))}
                    <option value="custom">Custom</option>
                  </select>
                </div>
                {!durationOptions.some((option) => option.days === Number(formData.durationDays)) && (
                  <div className="form-group">
                    <label>Custom Validity (Days) *</label>
                    <input
                      type="number"
                      value={formData.durationDays}
                      onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                      required
                      min="1"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                {editingPlan && (
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      {' '}Active
                    </label>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">
                    {editingPlan ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPlans;

